import React from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  GlobalStyles,
  Card,
  Grid,
} from "@mui/material";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbar from "devComponents/Navbars/PublicNavbar";
import bgImage from "assets/Logobackground.png";

const PrivacyPolicy = () => {
  const theme = {
    primary: "#1976d2",
    textMain: "#1a1a1a",
    textSecondary: "#555555",
    bgLight: "#f8f9fa",
  };

  const BulletPoint = ({ text }) => (
    <Box sx={{ display: "flex", mb: 1.5, gap: 1.5 }}>
      <Typography sx={{ color: theme.primary, fontWeight: "bold" }}>
        •
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: theme.textSecondary, lineHeight: 1.6 }}
      >
        {text}
      </Typography>
    </Box>
  );

  return (
    <>
      <GlobalStyles
        styles={{
          body: { backgroundColor: "#f4f7fe", margin: 0, padding: 0 },
          "*::selection": {
            backgroundColor: "rgba(25, 118, 210, 0.2)",
            color: "#000",
          },
        }}
      />

      <MKBox component="main">
        <MKBox
          display="flex"
          alignItems="center"
          minHeight="55vh"
          width="100%"
          sx={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <DefaultNavbar transparent light />
        </MKBox>

        <Container sx={{ mt: -10, mb: 10, position: "relative", zIndex: 3 }}>
          <Card
            sx={{
              p: { xs: 3, md: 8 },
              borderRadius: "1.25rem",
              boxShadow: ({ boxShadows: { xxl } }) => xxl,
              border: "1px solid rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(20px)",
            }}
          >
            <Box sx={{ maxWidth: "800px", mx: "auto" }}>
              <section>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, mb: 2, color: theme.textMain }}
                >
                  Our Commitment to Privacy
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ lineHeight: 1.8, color: theme.textSecondary }}
                >
                  At <strong>The DevHouse</strong>, we value your trust. This
                  policy outlines our practices regarding the collection, use,
                  and disclosure of your data when you interface with our
                  company and employee management systems.
                </Typography>
              </section>

              <Divider
                sx={{ my: 6, opacity: 0.1, backgroundColor: theme.textMain }}
              />

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      color: theme.primary,
                      letterSpacing: 2,
                    }}
                  >
                    01. Data Collection
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <BulletPoint text="Legal names and contact credentials" />
                    <BulletPoint text="Corporate entity documentation" />
                    <BulletPoint text="System access logs and IP addresses" />
                    <BulletPoint text="Role-based metadata" />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 800,
                      color: theme.primary,
                      letterSpacing: 2,
                    }}
                  >
                    02. Purpose of Use
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <BulletPoint text="Managing employee life-cycles" />
                    <BulletPoint text="Automating document expiry alerts" />
                    <BulletPoint text="Ensuring internal audit compliance" />
                    <BulletPoint text="Securing multi-role access controls" />
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{ mt: 6, display: "flex", flexDirection: "column", gap: 5 }}
              >
                <section>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    3. Security Measures
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.textSecondary, lineHeight: 1.7 }}
                  >
                    Your data is protected by enterprise-grade encryption. We
                    implement
                    <strong> 2FA (Two-Factor Authentication)</strong> for all
                    administrative actions and maintain detailed audit logs for
                    every document modification to ensure complete transparency
                    and security.
                  </Typography>
                </section>

                <section>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    4. Third-Party Sharing
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.textSecondary, lineHeight: 1.7 }}
                  >
                    We do not sell, trade, or rent your personal information.
                    Data is only shared with trusted infrastructure providers
                    necessary to operate the service, or when required by law.
                  </Typography>
                </section>
              </Box>

              <Box
                sx={{
                  mt: 10,
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                  background: theme.bgLight,
                  border: `1px dashed #ced4da`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  Questions or Concerns?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.textSecondary, mb: 2 }}
                >
                  Our legal team is available to assist with any data-related
                  inquiries.
                </Typography>
                <Typography
                  variant="h6"
                  component="a"
                  href="mailto:sales@thedevhouse.io"
                  sx={{
                    color: theme.primary,
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                >
                  sales@thedevhouse.io
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 8,
                textAlign: "center",
                color: "#999",
                letterSpacing: 2,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              &copy; 2026 devHouse Systems • All Rights Reserved
            </Typography>
          </Card>
        </Container>
      </MKBox>
    </>
  );
};

export default PrivacyPolicy;
